import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Check, Crown, AlertCircle } from 'lucide-react';
import { storage } from '../../lib/storage';
import { startCheckout } from '../../lib/razorpay';
import { toast } from 'sonner';

export const PremiumModal = ({ open, onOpenChange }) => {
  const prime = storage.get(storage.KEYS.PRIME, { active: false, plan: null });
  const [busy, setBusy] = useState(null);

  const buy = async (planId) => {
    setBusy(planId);
    try {
      const result = await startCheckout({ planId });
      toast.success(`${result.plan_label} activated! 🎉`, {
        description: `Valid till ${new Date(result.expires_at).toLocaleDateString()}`,
      });
      onOpenChange(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Payment failed';
      if (msg !== 'Payment cancelled') toast.error('Payment failed', { description: msg });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-card border-border" data-testid="premium-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[16px]">
            <Crown className="w-4 h-4 text-mint" /> Brahmastra Prime
          </DialogTitle>
          <DialogDescription className="text-[12px] text-muted-foreground">
            Unlock more chats, quizzes, and flashcards every day.
          </DialogDescription>
        </DialogHeader>

        {prime.active && (
          <div className="text-center">
            <span className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-mint text-[#08120e] font-bold">
              ACTIVE — {prime.plan?.label}
              {prime.expires_at && <> · till {new Date(prime.expires_at).toLocaleDateString()}</>}
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <PlanCard
            label="Test Mode"
            price="₹50"
            meta="7-day pass"
            features={['60 chats/day', '5 quizzes/day', '5 flashcards/day', 'No commitment']}
            onSelect={() => buy('test')}
            busy={busy === 'test'}
            testId="plan-test"
          />
          <PlanCard
            label="Prime Monthly"
            price="₹99"
            meta="30-day · unlimited"
            features={['Unlimited chats', 'Unlimited quizzes', 'Unlimited flashcards', 'All gurus unlocked']}
            onSelect={() => buy('monthly')}
            busy={busy === 'monthly'}
            featured
            testId="plan-monthly"
          />
        </div>

        <div className="text-[10.5px] text-muted-foreground flex items-start gap-1">
          <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
          Razorpay test mode — use test card 4111 1111 1111 1111, any future expiry, any CVV, OTP 1234.
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PlanCard = ({ label, price, meta, features, onSelect, featured, busy, testId }) => (
  <div className={`p-3 rounded-lg border ${featured ? 'border-mint/50 bg-mint/5' : 'border-border bg-secondary/30'} relative`} data-testid={testId}>
    {featured && <span className="absolute -top-2 left-3 text-[9px] font-bold px-2 py-0.5 rounded-full bg-mint text-[#08120e]">BEST VALUE</span>}
    <div className={`text-[11px] font-semibold uppercase tracking-wider ${featured ? 'text-mint' : 'text-bblue'}`}>{label}</div>
    <div className="text-2xl font-bold mt-1">{price}</div>
    <div className="text-[10.5px] text-muted-foreground">{meta}</div>
    <ul className="mt-2 space-y-1">
      {features.map((f, i) => (
        <li key={i} className="text-[11.5px] text-foreground/85 flex items-center gap-1.5">
          <Check className={`w-3 h-3 ${featured ? 'text-mint' : 'text-bblue'}`} /> {f}
        </li>
      ))}
    </ul>
    <Button
      size="sm"
      disabled={busy}
      className={`w-full mt-3 ${featured ? 'bg-mint text-[#08120e] hover:brightness-110' : 'bg-secondary hover:bg-secondary/80'}`}
      onClick={onSelect}
      data-testid={`${testId}-select`}
    >
      {busy ? 'Processing...' : `Pay ${price}`}
    </Button>
  </div>
);

export default PremiumModal;
